import React, { useContext, useMemo } from 'react';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { projectComparisonByYear } from '../../utils/monthlyProjection';
import { PlanReturnView, periodRangeLabel, rateOf, ageNote } from '../../utils/transferReturns';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { periodWithRange } from './projectionLegend';

/**
 * Comparativa de traspaso del dashboard (chirigota#576). La rentabilidad de los dos planes la
 * decide comparsa (`/plan-returns`, escenario TRANSFER, periodo común): aquí no hay tasas por
 * defecto ni se vuelve a restar la comisión, porque la rentabilidad ya es NETA. Proyección mes
 * a mes (aportación a final de mes) con el saldo, la aportación y los años del propio traspaso.
 */
interface PerformanceComparisonV2Props {
	/** Plan de Arca del traspaso (comparsa). */
	arca: PlanReturnView;
	/** Plan actual del usuario (comparsa). */
	origin: PlanReturnView;
	/** Los dos con el mismo periodo. */
	samePeriod: boolean;
	currentPlanName?: string;
	arcaPlanName?: string;
	/** Saldo que traspasa (€). */
	balance: number;
	/** Aportación mensual (€). */
	monthlyContribution: number;
	/** Años de la proyección (enteros). */
	years: number;
}

const PerformanceComparisonV2: React.FC<PerformanceComparisonV2Props> = ({
	arca,
	origin,
	samePeriod,
	currentPlanName,
	arcaPlanName,
	balance,
	monthlyContribution,
	years
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const currentPlanReturn = origin.annualPct;
	const arcaReturn = arca.annualPct;
	const maxReturn = Math.max(currentPlanReturn, arcaReturn) || 1;
	const currentBarWidth = (Math.max(currentPlanReturn, 0) / maxReturn) * 100;
	const arcaBarWidth = (Math.max(arcaReturn, 0) / maxReturn) * 100;

	const planName = currentPlanName || translate('individual.dashboard.comparison.currentPlan');
	const arcaName = arcaPlanName || 'Arca';

	// `plan` = Arca, `bank` = plan actual. Mes a mes con la tasa mensual neta de cada uno.
	const projectionData = useMemo(
		() =>
			projectComparisonByYear(balance, monthlyContribution, rateOf(arca), rateOf(origin), years),
		[balance, monthlyContribution, arca, origin, years]
	);

	const last = projectionData[projectionData.length - 1];
	const projectionDiff = last.plan - last.bank;
	// Solo se habla de «coste de oportunidad» si traspasar sale a cuenta.
	const arcaAhead = projectionDiff > 0;
	const withYears = (key: string) => translate(key).replace('{years}', String(years));
	const pct = (n: number) => `${n > 0 ? '+' : ''}${fmtNum(n)}%`;

	const fmtEur = (n: number) =>
		n.toLocaleString('es-ES', {
			style: 'currency',
			currency: 'EUR',
			maximumFractionDigits: 0
		});

	const fmtK = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center pl-2">
				<h2 className="text-2xl font-bold font-heading text-foreground leading-8">
					{translate('individual.dashboard.comparison.title')}
				</h2>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Card izquierda — Comparativa histórica */}
				<div className="surface-card p-6 lg:p-8 flex flex-col justify-between min-h-[400px]">
					<div>
						<h3 className="text-lg font-bold font-heading text-foreground leading-7">
							{translate('individual.dashboard.comparison.pastTitle')}
						</h3>
						<p className="text-xs text-muted-foreground mt-1.5 leading-[19.5px]">
							{translate(
								arcaAhead
									? 'individual.dashboard.comparison.pastDescription'
									: 'individual.dashboard.comparison.pastDescriptionNeutral'
							)}
						</p>
					</div>

					<div className="py-6 flex flex-col gap-0">
						{/* Plan actual */}
						<div className="flex flex-col gap-2">
							<div className="flex items-end justify-between">
								<span className="text-sm font-medium text-muted-foreground">{planName}</span>
								<span className="text-sm leading-5">
									<span className="font-bold text-foreground">{pct(currentPlanReturn)}</span>{' '}
									<span className="text-xs text-foreground">
										{translate('individual.dashboard.comparison.annual')}
										{ageNote(translate, origin)}
									</span>
								</span>
							</div>
							<div className="h-4 w-full bg-arca-pink-light/30 rounded-full overflow-hidden">
								<div
									className="h-full bg-secondary rounded-full transition-all duration-700"
									style={{ width: `${currentBarWidth}%` }}
									data-testid="bar-current"
								/>
							</div>
						</div>

						{/* Plan Arca */}
						<div className="flex flex-col gap-2 pt-6">
							<div className="flex items-end justify-between">
								<span className="text-sm font-medium text-foreground">
									{translate('individual.dashboard.comparison.ifTransferred').replace(
										'{plan}',
										arcaName
									)}
								</span>
								<span className="text-sm leading-5 text-primary">
									<span className="font-bold">{pct(arcaReturn)}</span>{' '}
									<span className="text-xs">
										{translate('individual.dashboard.comparison.annual')}
										{ageNote(translate, arca)}
									</span>
								</span>
							</div>
							<div className="h-4 w-full bg-arca-green-light/30 rounded-full overflow-hidden">
								<div
									className="h-full bg-primary rounded-full transition-all duration-700"
									style={{ width: `${arcaBarWidth}%` }}
									data-testid="bar-arca"
								/>
							</div>
						</div>
					</div>

					{/* Coste de oportunidad: lo que dejaría de ganar sin traspasar (solo si Arca va por delante) */}
					{arcaAhead && (
						<div className="border-t border-muted pt-4 flex items-center justify-between">
							<div className="flex items-center gap-2">
								<AlertCircle className="w-5 h-5 text-secondary" />
								<span className="text-sm font-semibold text-foreground">
									{translate('individual.dashboard.comparison.opportunityCost')}
								</span>
							</div>
							<span className="text-xl font-bold text-secondary">-{fmtEur(projectionDiff)}</span>
						</div>
					)}
				</div>

				{/* Card derecha — Proyección futura con Recharts */}
				<div className="gradient-arca-green border border-primary/10 rounded-(--radius) shadow-(--shadow-card) p-6 lg:p-8 flex flex-col justify-between min-h-[400px]">
					<div className="flex items-center justify-between mb-2">
						<h3 className="text-lg font-bold font-heading text-foreground leading-7">
							{translate('individual.dashboard.comparison.futureTitle')}
						</h3>
						<div className="flex items-center gap-1 text-xs text-muted-foreground">
							<TrendingUp className="w-3.5 h-3.5 text-primary" />
							{withYears('individual.dashboard.comparison.projectionYears')}
						</div>
					</div>
					<p className="text-xs text-muted-foreground mb-3 leading-[19.5px]">
						{translate(
							arcaAhead
								? 'individual.dashboard.comparison.futureDescription'
								: 'individual.dashboard.comparison.futureDescriptionNeutral'
						)}
					</p>

					{/* Leyenda */}
					<div className="flex gap-4 text-xs text-muted-foreground mb-2">
						<div className="flex items-center gap-1.5">
							<div className="w-4 h-0.5 bg-primary rounded" />
							{arcaName}
						</div>
						<div className="flex items-center gap-1.5">
							<div className="w-4 h-0.5 bg-muted-foreground/30 rounded" />
							{planName}
						</div>
					</div>

					{/* Recharts projection */}
					<div className="flex-1 min-h-[180px]">
						<ResponsiveContainer width="100%" height="100%" minHeight={180}>
							<AreaChart data={projectionData}>
								<defs>
									<linearGradient id="gradProjection" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2} />
										<stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
									</linearGradient>
								</defs>
								<XAxis
									dataKey="year"
									tickFormatter={(v) => {
										if (v === 0) return translate('individual.dashboard.comparison.today');
										if (v % 5 === 0)
											return `${translate('individual.dashboard.comparison.year')} ${v}`;
										return '';
									}}
									tick={{ fontSize: 10 }}
								/>
								<YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} width={40} />
								<Tooltip
									formatter={(v, name) => [
										fmtEur(Number(v)),
										name === 'plan' ? arcaName : planName
									]}
								/>
								<Area
									type="monotone"
									dataKey="bank"
									stroke="var(--muted-foreground)"
									strokeWidth={1.5}
									strokeDasharray="5 3"
									fill="none"
								/>
								<Area
									type="monotone"
									dataKey="plan"
									stroke="var(--primary)"
									strokeWidth={2}
									fill="url(#gradProjection)"
								/>
							</AreaChart>
						</ResponsiveContainer>
					</div>

					{/* Pie: diferencia estimada, comisión de gestión y de dónde salen las rentabilidades */}
					<div className="border-t border-primary/10 pt-3 mt-2">
						{arcaAhead && (
							<>
								<p className="text-center text-xs text-muted-foreground">
									{withYears('individual.dashboard.comparison.estimatedDiff')}
								</p>
								<p className="text-center font-heading font-bold text-2xl text-primary mt-1">
									+{fmtEur(projectionDiff)}
								</p>
							</>
						)}
						<div className="flex justify-center gap-6 text-[10px] text-muted-foreground mt-2">
							{origin.managementFeePct !== null && (
								<span>
									{translate('individual.dashboard.comparison.originCommission')}{' '}
									{fmtNum(origin.managementFeePct)}%
								</span>
							)}
							{arca.managementFeePct !== null && (
								<span>
									{translate('individual.dashboard.comparison.arcaCommission').replace(
										'{plan}',
										arcaName
									)}{' '}
									{fmtNum(arca.managementFeePct)}%
								</span>
							)}
						</div>
						<p className="text-center text-[10px] text-muted-foreground mt-2">
							{samePeriod
								? translate('individual.transferProjection.returnPeriod').replace(
										'{period}',
										periodWithRange(
											translate,
											arca.periodYears,
											periodRangeLabel(translate, arca.period)
										)
									)
								: translate('individual.transferProjection.returnPeriodDifferent')}{' '}
							{translate('individual.transferProjection.disclaimer')}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PerformanceComparisonV2;
